import { useCallback } from "react";
import { signInWithPopup, signOut } from "firebase/auth";
import { auth, googleProvider } from "../../services/firebase";

const CANCELLED = new Set(["auth/popup-closed-by-user", "auth/cancelled-popup-request", "auth/user-cancelled"]);

const friendlyError = (err) => {
  if (CANCELLED.has(err?.code)) return "Google sign-in was cancelled.";
  if (err?.code === "auth/popup-blocked") return "Your browser blocked the Google popup. Please allow popups and try again.";
  if (err?.code === "auth/unauthorized-domain") return "Google sign-in isn't enabled for this website yet.";
  return err?.message || "Google sign-in failed.";
};

// Google popup sign-in through Firebase (same setup as the business portal).
// Resolves { name, email, googleId, idToken } for the API's /google-signin.
// The API only trusts the Firebase ID token (verified server-side); the other
// fields are just display hints.
export const useGoogleSignIn = () => {
  const signIn = useCallback(async () => {
    try {
      const { user } = await signInWithPopup(auth, googleProvider);
      if (!user?.email) throw new Error("Your Google account has no email address.");
      const idToken = await user.getIdToken();
      const account = { name: user.displayName || user.email.split("@")[0], email: user.email, googleId: user.uid, idToken };
      // Only the Legal Platform token is kept; don't leave a Firebase session behind.
      signOut(auth).catch(() => {});
      return account;
    } catch (err) {
      throw new Error(friendlyError(err));
    }
  }, []);

  return { enabled: true, ready: true, signIn };
};
